import {
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  ChangeEvent,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Typography } from '../typography';
import {
  CellParent,
  Container,
  HeaderWrapper,
  Invalidator,
  ItemWrapper,
  ListActionContainer,
  ListActionWrapper,
  ListContainer,
  ListFooter,
  ListHeader,
  ListMessageWrapper,
  ListWrapper,
  StyledTablePagination,
} from './List.styles';
import _ from 'lodash';
import {
  ExpandLess,
  ExpandMore,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Remove,
} from '@mui/icons-material';
import { CustomTooltip } from '../Tooltip/customTooltip';

export type DataKey<T> = keyof T;

export interface Field<T> {
  title: ReactNode | string; //header display title
  key?: DataKey<T> | string; //object key
  path?: string[]; //if nested field
  valueGetter?: (item: T) => string | number;
  cellRenderer?: (item: T) => ReactNode;
  tooltip?: boolean | ((item: T) => ReactNode);
  sortable?: boolean;
}

export interface SortElement<T> {
  key: DataKey<T> | string;
  type?: 'desc' | 'asc';
}

const COLLAPSED_SIZE = '10px';

interface ListProps<T> {
  list: T[];
  onPageSizeChange: (newPageSize: number) => void;
  onPageChange: (newPage: number) => void;
  fields: Field<T>[];
  rowRenderer?: ({ ...rest }: RowRendererProps<T>) => ReactNode;
  page: number;
  count: number;
  pageSize: number;
  loading?: boolean;
  isPreviousData?: boolean;
  sortBy?: SortElement<T>[];
  onSortClick?: (clicked: SortElement<T>) => void;
  keyGetter: (item: T, idx: number) => string;
  onItemClick?: (item: T) => void;
  actions?: ReactNode;
  collapsibleColumns?: boolean;
  actionsHeader?: string;
  gridTemplateColumns?: string;
  rowsPerPageOptions?: (
    | number
    | {
        value: number;
        label: string;
      }
  )[];
  selectedIds?: string[];
  onSelectedIdsChange?: (newIds: string[]) => void;
  keepSelectedIds?: boolean;
  onlyOne?: boolean;
  getSelectedIdsCountLabel?: (selectedIds: number) => string;
  maxheight?: string;
  emptyListMessage?: string;
  alternate?: 'columns' | 'rows';
  hideFooter?: boolean;
  keepRecentlyOpenedItem?: boolean;
  lastOpenedItem?: T;
}

interface RowRendererProps<T> {
  item: T;
  key: string;
  selected: boolean;
  handleCheckboxChange: (e: ChangeEvent, checked: boolean) => void;
  handleClick: () => void;
  gridTemplateColumns: string;
  isSelection: boolean;
}

export const List = <T extends unknown>({
  list,
  onPageSizeChange,
  onPageChange,
  keyGetter,
  fields,
  rowRenderer,
  count,
  page,
  pageSize,
  loading,
  onItemClick,
  actions,
  actionsHeader,
  gridTemplateColumns,
  rowsPerPageOptions,
  selectedIds,
  onSelectedIdsChange,
  onlyOne,
  keepSelectedIds,
  getSelectedIdsCountLabel,
  maxheight,
  emptyListMessage,
  collapsibleColumns,
  alternate = 'rows',
  onSortClick,
  sortBy,
  isPreviousData,
  hideFooter,
  keepRecentlyOpenedItem,
  lastOpenedItem,
}: ListProps<T>): ReactElement => {
  const [checkAll, setCheckAll] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const getGridTemplateColumns = useCallback(() => {
    let ret = '';
    if (gridTemplateColumns) {
      ret = gridTemplateColumns;
    } else {
      let columns: string[] = [];
      fields.forEach(() => {
        columns.push('1fr');
      });
      ret = columns.join(' ');
    }
    if (selectedIds) {
      ret = '0.5fr ' + ret;
    }
    return ret;
  }, [fields, gridTemplateColumns, selectedIds]);

  const [gridColumns, setGTC] = useState(getGridTemplateColumns());

  useEffect(() => {
    setGTC(getGridTemplateColumns());
  }, [getGridTemplateColumns, gridTemplateColumns]);

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [page, pageSize]);

  const handleCheckAllChange = (_event: ChangeEvent, checked: boolean) => {
    if (onSelectedIdsChange) {
      if (checked) {
        let ids = list.map((el, idx) => keyGetter(el, idx));
        if (keepSelectedIds) {
          let intersection = _.union(selectedIds, ids);
          onSelectedIdsChange(intersection);
        } else {
          onSelectedIdsChange(ids);
        }
      } else {
        if (keepSelectedIds) {
          let ids = list.map((el, idx) => keyGetter(el, idx));
          if (selectedIds) {
            let selectedIdsTemp = [...selectedIds];
            _.remove(selectedIdsTemp, (el) => ids.includes(el));
            onSelectedIdsChange(selectedIdsTemp);
          }
        } else {
          onSelectedIdsChange([]);
        }
      }
    }
  };

  useEffect(() => {
    if (loading) {
      setCheckAll(false);
    } else {
      let ids = list.map((el, idx) => keyGetter(el, idx));
      let allChecked = true;
      for (let index = 0; index < ids.length; index++) {
        if (!selectedIds?.includes(ids[index])) {
          allChecked = false;
          break;
        }
      }
      setCheckAll(allChecked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, list, loading]);

  const renderLoading = (): ReactNode[] => {
    let els: ReactNode[] = [];
    for (let i = 0; i < pageSize; i++) {
      els.push(
        <ItemWrapper alternate={alternate === 'rows'} gridTemplateColumns={gridColumns} key={i}>
          {selectedIds && (
            <CellParent alternate={alternate === 'columns'}>
              <Checkbox />
            </CellParent>
          )}
          {fields.map(() => (
            <CellParent alternate={alternate === 'columns'}>
              <Skeleton width={'80%'} />
            </CellParent>
          ))}
        </ItemWrapper>,
      );
    }
    return els;
  };

  const renderListContent = (list: T[], loading?: boolean, emptyMessage?: string) => {
    if (loading) {
      return renderLoading();
    }
    if (list.length > 0) {
      return list.map((el, idx) => {
        if (idx === 0) {
          return <div ref={ref}>{renderItem(el, idx)}</div>;
        } else {
          return renderItem(el, idx);
        }
      });
    }
    if (emptyMessage) {
      return (
        <ListMessageWrapper>
          <Typography fontSize={'40px'} p={'50px'}>
            {emptyMessage}
          </Typography>
        </ListMessageWrapper>
      );
    }
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    onPageChange(newPage);
    if (!keepSelectedIds && onSelectedIdsChange) {
      onSelectedIdsChange([]);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onPageSizeChange(parseInt(event.target.value, 10));
  };

  const [clickedItem, setClickedItem] = useState<T>();
  const handleItemClick = (item: T) => {
    if (onItemClick) {
      if (keepRecentlyOpenedItem) setClickedItem(item);
      onItemClick(item);
    }
  };

  useEffect(() => {
    if (lastOpenedItem) {
      setClickedItem(lastOpenedItem);
    }
  }, [lastOpenedItem]);

  const renderItem = (item: T, idx: number) => {
    const handleCheckboxChange = (e: ChangeEvent, checked: boolean) => {
      e.stopPropagation();
      let temp = selectedIds ? [...selectedIds] : [];
      if (onlyOne) {
        if (checked) {
          onSelectedIdsChange && onSelectedIdsChange([keyGetter(item, idx)]);
        } else {
          onSelectedIdsChange && onSelectedIdsChange([]);
        }
      } else {
        if (checked) {
          temp.push(keyGetter(item, idx));
          onSelectedIdsChange && onSelectedIdsChange(temp);
        } else {
          onSelectedIdsChange &&
            onSelectedIdsChange(temp.filter((e) => e !== keyGetter(item, idx)));
        }
      }
    };

    const handleClick = () => {
      handleItemClick(item);
    };

    const renderCell = (field: Field<T>, selectedRow?: boolean) => {
      let value: string | number | ReactNode = 'ND';
      if (field.cellRenderer) {
        value = field.cellRenderer(item);
      } else if (field.valueGetter) {
        value = field.valueGetter(item);
      } else if (field.key) {
        value = field.path ? _.get(item, [field.key, ...field.path]) : _.get(item, field.key);
      }

      let tooltipTitle: string | ReactNode = '';

      if (field.tooltip) {
        if (field.tooltip === true) {
          tooltipTitle = value;
        } else {
          tooltipTitle = field.tooltip(item);
        }
      }

      if (!isValidElement(value)) {
        value = <Typography truncate>{value}</Typography>;
      }

      if (tooltipTitle) {
        return (
          <CustomTooltip title={tooltipTitle}>
            <CellParent alternate={alternate === 'columns'} selected={selectedRow}>
              <Typography truncate>{value}</Typography>
            </CellParent>
          </CustomTooltip>
        );
      } else {
        return (
          <CellParent alternate={alternate === 'columns'} selected={selectedRow}>
            <Typography truncate>{value}</Typography>
          </CellParent>
        );
      }
    };

    const selected = selectedIds?.includes(keyGetter(item, idx));

    if (rowRenderer) {
      return rowRenderer({
        item: item,
        key: keyGetter(item, idx),
        gridTemplateColumns: gridColumns,
        handleCheckboxChange: handleCheckboxChange,
        handleClick: handleClick,
        isSelection: selectedIds !== undefined,
        selected: selected ?? false,
      });
    } else {
      return (
        <ItemWrapper
          alternate={alternate === 'rows'}
          selected={selected}
          gridTemplateColumns={gridColumns}
          key={keyGetter(item, idx)}
          onClick={handleClick}
          isRecentlyOpened={item === clickedItem}
        >
          {selectedIds && (
            <CellParent alternate={alternate === 'columns'} selected={selected}>
              <Checkbox
                size="small"
                onClick={(e) => e.stopPropagation()}
                checked={selected}
                onChange={handleCheckboxChange}
              />
            </CellParent>
          )}
          {fields.map((el, idx) => (isCollapsed(idx) ? <div /> : renderCell(el, selected)))}
        </ItemWrapper>
      );
    }
  };

  const onHeaderClick = (f: Field<T>, idx: number) => {
    if (collapsibleColumns) {
      setGTC((prev) => {
        let parts = prev.split(' ');
        if (selectedIds) {
          idx++;
        }
        let current = parts[idx];
        if (current === '1fr') {
          parts[idx] = COLLAPSED_SIZE;
        } else {
          parts[idx] = '1fr';
        }
        return parts.join(' ');
      });
    }
    if (f.sortable && f.key && onSortClick) {
      const found = sortBy?.filter((s) => s.key === f.key);
      if (found && found.length === 1) {
        onSortClick({ key: f.key, type: found[0].type });
      } else {
        onSortClick({ key: f.key, type: undefined });
      }
    }
  };

  const isCollapsed = (idx: number) => {
    let parts = gridColumns.split(' ');
    if (selectedIds) {
      idx++;
    }
    let current = parts[idx];
    return current === COLLAPSED_SIZE;
  };

  const renderSortIcon = useCallback(
    (f: Field<T>) => {
      if (!f.sortable) {
        return undefined;
      }
      if (sortBy?.some((s) => s.key === f.key)) {
        const t = sortBy.filter((s) => s.key === f.key);
        if (t[0].type === 'desc') {
          return <ExpandMore />;
        } else {
          return <ExpandLess />;
        }
      } else {
        return <Remove />;
      }
    },
    [sortBy],
  );

  const calculateLastPage = (): number => {
    return Math.ceil(count / pageSize) - 1;
  };
  return (
    <>
      <Container>
        {actions && (
          <ListActionWrapper>
            <Stack alignItems={'center'}>
              <Typography>{actionsHeader ?? '`actionsHeader` not defined'}</Typography>
              <ListActionContainer>
                <Stack
                  direction="row"
                  alignItems={'center'}
                  spacing={3}
                  sx={{ paddingX: '10px' }}
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  {actions}
                </Stack>
              </ListActionContainer>
            </Stack>
          </ListActionWrapper>
        )}
        <ListWrapper corner={isValidElement(actions)}>
          {isPreviousData && (
            <Invalidator>
              <CircularProgress size={list.length > 5 ? 100 : 30} />
            </Invalidator>
          )}
          <ListHeader gridTemplateColumns={gridColumns}>
            {selectedIds && (
              <HeaderWrapper>
                {!onlyOne && (
                  <Checkbox size="small" checked={checkAll} onChange={handleCheckAllChange} />
                )}
              </HeaderWrapper>
            )}
            {fields.map((el, idx) => (
              <HeaderWrapper
                key={idx}
                py={selectedIds && !onlyOne ? undefined : '5px'}
                disabled={loading ? 1 : 0}
                sortable={el.sortable ? 1 : 0}
                alternate={alternate === 'columns'}
                collapsible={collapsibleColumns ? 1 : 0}
                collapsed={isCollapsed(idx) ? 1 : 0}
                onClick={() => !loading && onHeaderClick(el, idx)}
              >
                <Stack
                  direction="row"
                  spacing="2px"
                  fontWeight={600}
                  alignItems="center"
                  height="inherit"
                >
                  {isValidElement(el.title) ? (
                    el.title
                  ) : (
                    <Typography paddingY={1} noWrap variant="subtitle2">
                      {el.title}
                    </Typography>
                  )}
                  {renderSortIcon(el)}
                </Stack>
              </HeaderWrapper>
            ))}
          </ListHeader>
          <ListContainer>{renderListContent(list, loading, emptyListMessage)}</ListContainer>
          {!hideFooter && (
            <ListFooter>
              {selectedIds && getSelectedIdsCountLabel && (
                <div>
                  <Typography>{getSelectedIdsCountLabel(selectedIds.length)}</Typography>
                </div>
              )}
              <StyledTablePagination
                sx={{ color: 'primary.main' }}
                count={count}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions ?? [15, 25, 50, 100]}
              />
              <IconButton
                color={'primary'}
                disabled={page === 0}
                onClick={(e) => handleChangePage(e, 0)}
              >
                <KeyboardDoubleArrowLeft />
              </IconButton>
              <IconButton
                color={'primary'}
                disabled={page === calculateLastPage() || count === 0}
                onClick={(e) => handleChangePage(e, calculateLastPage())}
              >
                <KeyboardDoubleArrowRight />
              </IconButton>
              <div></div>
            </ListFooter>
          )}
        </ListWrapper>
      </Container>
    </>
  );
};
